/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * Developer settings: OAuth clients for third-party integrations.
 *
 * Two tabs. Clients lists the organisation's OAuth clients: create one, rotate
 * its secret, suspend or revoke it, change its integration user's role, and see
 * who has authorised it. Activity is the audit trail (created, rotated, token
 * issued, failed sign-ins).
 *
 * The API_ACCESS add-on gates the page; MANAGE_INTEGRATIONS (system admin until
 * the organisation enforces roles) gates every call behind it.
 */

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
} from "@mui/material";
import { Add, ArrowBack, Key, Lock, MoreVert } from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import {
  ClientEditorDialog,
  GRANT_LABELS,
  SecretRevealDialog,
} from "components/SettingsComponents/DeveloperClientDialogs";
import { useAuth } from "auth/Auth";
import { useModuleEntitlements } from "hooks/useModuleEntitlements";
import colors from "styles/tokens/colors";
import { getApiErrorMessage } from "utils/apiErrors";
import { getApiBaseUrl } from "utils/apiBaseUrl";
import { useTheme } from "@mui/material/styles";
import PageHeader from "components/v2/PageHeader";

const CARD_SX = {
  p: 3,
  border: `1px solid ${colors.callCenter.cardBorder}`,
  boxShadow: "none",
  borderRadius: "12px",
};

const STATUS_CHIP = {
  active: colors.status.success,
  suspended: colors.status.warning,
  revoked: colors.status.neutral,
};

const EVENT_LABELS = {
  "client.created": "Client created",
  "client.updated": "Client changed",
  "client.secret_rotated": "Secret rotated",
  "client.suspended": "Client suspended",
  "client.reactivated": "Client reactivated",
  "client.revoked": "Client revoked",
  "integration_user.created": "Integration user created",
  "integration_user.updated": "Integration user role changed",
  "consent.granted": "Access granted by a user",
  "consent.revoked": "Access withdrawn by a user",
  "grant.revoked": "Authorisation revoked",
  "token.issued": "Token issued",
  "token.refreshed": "Token refreshed",
  "token.reuse_detected": "Refresh token reused (all its tokens revoked)",
  "token.revoked": "Token revoked",
  "client_auth.failed": "Failed client sign-in",
  "assertion.rejected": "Signed assertion rejected",
  "rate_limited": "Rate limited",
};

const when = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

function StatusChip({ status }) {
  const chip = STATUS_CHIP[status] || colors.status.neutral;
  return (
    <Chip size="small" label={status}
      sx={{ backgroundColor: chip.background, color: chip.text, fontWeight: 500, textTransform: "capitalize" }} />
  );
}

function ConfirmDialog({ open, title, body, confirmLabel, danger, onCancel, onConfirm, busy }) {
  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <MDTypography variant="body2">{body}</MDTypography>
      </DialogContent>
      <DialogActions>
        <MDButton variant="outlined" color="secondary" onClick={onCancel} disabled={busy}>Cancel</MDButton>
        <MDButton variant="gradient" color={danger ? "error" : "info"} onClick={onConfirm} disabled={busy}>
          {busy ? "Working…" : confirmLabel}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

function GrantsDialog({ open, client, onClose }) {
  const { axiosInstance } = useAuth();
  const [grants, setGrants] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!client) return;
    try {
      const res = await axiosInstance.get(`/developer/clients/${client.client_id}/grants`);
      setGrants(res.data || []);
    } catch (e) {
      setError(getApiErrorMessage(e, "Could not load authorisations."));
    }
  }, [axiosInstance, client]);

  useEffect(() => {
    if (!open) return;
    setGrants(null);
    load();
  }, [open, load]);

  const revoke = async (id) => {
    try {
      await axiosInstance.delete(`/developer/grants/${id}`);
      load();
    } catch (e) {
      setError(getApiErrorMessage(e, "Could not revoke."));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Who has authorised {client?.name}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Table size="small">
          <TableHead sx={{ display: "table-header-group" }}>
            <TableRow>
              <TableCell>Acting as</TableCell>
              <TableCell>How</TableCell>
              <TableCell>Scopes</TableCell>
              <TableCell>Last used</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {grants === null && !error && (
              <TableRow><TableCell colSpan={5} align="center"><CircularProgress size={24} /></TableCell></TableRow>
            )}
            {grants?.length === 0 && (
              <TableRow><TableCell colSpan={5}>No one has authorised this client yet.</TableCell></TableRow>
            )}
            {(grants || []).map((g) => (
              <TableRow key={g.id}>
                <TableCell>{g.user_name}{g.email ? ` (${g.email})` : ""}</TableCell>
                <TableCell>{g.grant_kind === "consent" ? "User consent" : "Server to server"}</TableCell>
                <TableCell>{(g.scopes || []).length} scopes</TableCell>
                <TableCell>{when(g.last_used_at)}</TableCell>
                <TableCell align="right">
                  <MDButton size="small" variant="text" color="error" onClick={() => revoke(g.id)}>Revoke</MDButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <MDButton variant="outlined" color="secondary" onClick={onClose}>Close</MDButton>
      </DialogActions>
    </Dialog>
  );
}

function ClientsTab() {
  const { axiosInstance } = useAuth();
  const [clients, setClients] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editor, setEditor] = useState({ open: false, client: null });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [secret, setSecret] = useState(null);
  const [menu, setMenu] = useState({ anchor: null, client: null });
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [grantsFor, setGrantsFor] = useState(null);
  const [usage, setUsage] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const [c, s, r, u] = await Promise.allSettled([
      axiosInstance.get("/developer/clients", { params: { include_revoked: true } }),
      axiosInstance.get("/developer/scopes"),
      axiosInstance.get("/developer/roles"),
      axiosInstance.get("/developer/usage", { params: { days: 30 } }),
    ]);
    if (u.status === "fulfilled") setUsage(u.value.data?.totals || {});
    if (c.status === "fulfilled") setClients(c.value.data || []);
    else setError(getApiErrorMessage(c.reason, "Could not load clients."));
    if (s.status === "fulfilled") setCatalog(s.value.data?.scopes || []);
    if (r.status === "fulfilled") setRoles(r.value.data || []);
    setLoading(false);
  }, [axiosInstance]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (body, newRoleId) => {
    setSaving(true);
    setSaveError("");
    try {
      if (editor.client) {
        const id = editor.client.client_id;
        if (newRoleId) {
          await axiosInstance.put(`/developer/clients/${id}/integration-user`, { role_id: newRoleId });
        }
        await axiosInstance.patch(`/developer/clients/${id}`, body);
      } else {
        const res = await axiosInstance.post("/developer/clients", body);
        setSecret({ clientId: res.data.client_id, secret: res.data.client_secret });
      }
      setEditor({ open: false, client: null });
      load();
    } catch (e) {
      setSaveError(getApiErrorMessage(e, "Could not save the client."));
    } finally {
      setSaving(false);
    }
  };

  const runConfirmed = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      const id = confirm.client.client_id;
      if (confirm.kind === "rotate") {
        const res = await axiosInstance.post(`/developer/clients/${id}/rotate-secret`, { grace_hours: 24 });
        setSecret({ clientId: id, secret: res.data.client_secret });
      } else if (confirm.kind === "revoke") {
        await axiosInstance.post(`/developer/clients/${id}/revoke`);
      } else if (confirm.kind === "suspend" || confirm.kind === "reactivate") {
        await axiosInstance.patch(`/developer/clients/${id}`, {
          status: confirm.kind === "suspend" ? "suspended" : "active",
        });
      }
      setConfirm(null);
      load();
    } catch (e) {
      setError(getApiErrorMessage(e, "That did not work."));
      setConfirm(null);
    } finally {
      setBusy(false);
    }
  };

  const openMenu = (e, client) => setMenu({ anchor: e.currentTarget, client });
  const closeMenu = () => setMenu({ anchor: null, client: null });
  const ask = (kind) => {
    const client = menu.client;
    closeMenu();
    const copy = {
      rotate: {
        title: "Rotate client secret?",
        body: "A new secret is created and shown once. The current secret keeps working for 24 hours so you can update your integration.",
        confirmLabel: "Rotate",
      },
      suspend: {
        title: `Suspend ${client.name}?`,
        body: "It cannot get new tokens, and tokens it already has stop working within a minute. You can reactivate it later.",
        confirmLabel: "Suspend",
        danger: true,
      },
      reactivate: { title: `Reactivate ${client.name}?`, body: "It can get tokens again.", confirmLabel: "Reactivate" },
      revoke: {
        title: `Revoke ${client.name} permanently?`,
        body: "Every authorisation and token is revoked and the client can never be used again. Records it created keep showing it as their creator.",
        confirmLabel: "Revoke",
        danger: true,
      },
    }[kind];
    setConfirm({ kind, client, ...copy });
  };

  if (loading) return <MDBox py={6} textAlign="center"><CircularProgress /></MDBox>;

  return (
    <>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Card sx={CARD_SX}>
        <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <MDBox>
            <MDTypography variant="h6">OAuth clients</MDTypography>
            <MDTypography variant="body2" color="text">
              Each client is one integration. Give it only the scopes it needs.
            </MDTypography>
          </MDBox>
          <MDButton variant="gradient" color="info" startIcon={<Add />}
            onClick={() => { setSaveError(""); setEditor({ open: true, client: null }); }}>
            New client
          </MDButton>
        </MDBox>
        <MDBox sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead sx={{ display: "table-header-group" }}>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Client ID</TableCell>
                <TableCell>Grants</TableCell>
                <TableCell>Runs as</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Calls (30 days)</TableCell>
                <TableCell>Created</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {clients.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8}>
                    <MDTypography variant="body2" color="text">
                      No clients yet. Create one to let an integration call the PearlCRM API.
                    </MDTypography>
                  </TableCell>
                </TableRow>
              )}
              {clients.map((c) => (
                <TableRow key={c.client_id} sx={{ opacity: c.status === "revoked" ? 0.6 : 1 }}>
                  <TableCell>
                    <MDTypography variant="button" fontWeight="medium">{c.name}</MDTypography>
                    <MDTypography variant="caption" color="text" display="block">
                      {c.client_type === "public" ? "Public client" : "Confidential client"}
                    </MDTypography>
                  </TableCell>
                  <TableCell sx={{ fontFamily: "monospace", whiteSpace: "nowrap" }}>{c.client_id}</TableCell>
                  <TableCell>
                    {(c.grant_types || []).map((g) => (
                      <Tooltip key={g} title={GRANT_LABELS[g] || g}>
                        <Chip size="small" label={g.replace(/_/g, " ")} sx={{ mr: 0.5, mb: 0.5 }} />
                      </Tooltip>
                    ))}
                  </TableCell>
                  <TableCell>{c.integration_role ? c.integration_role.name : "—"}</TableCell>
                  <TableCell><StatusChip status={c.status} /></TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {Number(usage[c.client_id] || 0).toLocaleString()}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{when(c.created_at)}</TableCell>
                  <TableCell align="right">
                    {c.status !== "revoked" && (
                      <IconButton onClick={(e) => openMenu(e, c)} aria-label={`Actions for ${c.name}`}>
                        <MoreVert />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </MDBox>
      </Card>

      <Menu anchorEl={menu.anchor} open={Boolean(menu.anchor)} onClose={closeMenu}>
        <MenuItem onClick={() => { const c = menu.client; closeMenu(); setSaveError(""); setEditor({ open: true, client: c }); }}>
          Edit
        </MenuItem>
        <MenuItem onClick={() => { const c = menu.client; closeMenu(); setGrantsFor(c); }}>Authorisations</MenuItem>
        {menu.client?.client_type === "confidential" && <MenuItem onClick={() => ask("rotate")}>Rotate secret</MenuItem>}
        {menu.client?.status === "active" && <MenuItem onClick={() => ask("suspend")}>Suspend</MenuItem>}
        {menu.client?.status === "suspended" && <MenuItem onClick={() => ask("reactivate")}>Reactivate</MenuItem>}
        <MenuItem onClick={() => ask("revoke")} sx={{ color: colors.callCenter.danger }}>Revoke</MenuItem>
      </Menu>

      <ClientEditorDialog
        open={editor.open}
        client={editor.client}
        catalog={catalog}
        roles={roles}
        saving={saving}
        error={saveError}
        onClose={() => setEditor({ open: false, client: null })}
        onSave={save}
      />
      <SecretRevealDialog open={Boolean(secret)} clientId={secret?.clientId} secret={secret?.secret}
        onClose={() => setSecret(null)} />
      <ConfirmDialog open={Boolean(confirm)} {...(confirm || {})} busy={busy}
        onCancel={() => setConfirm(null)} onConfirm={runConfirmed} />
      <GrantsDialog open={Boolean(grantsFor)} client={grantsFor} onClose={() => setGrantsFor(null)} />
    </>
  );
}

function ActivityTab() {
  const { axiosInstance } = useAuth();
  const [events, setEvents] = useState(null);
  const [next, setNext] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async (beforeId) => {
    try {
      const res = await axiosInstance.get("/developer/events", { params: { before_id: beforeId || undefined } });
      setEvents((prev) => (beforeId ? [...(prev || []), ...res.data.events] : res.data.events));
      setNext(res.data.next_before_id);
    } catch (e) {
      setError(getApiErrorMessage(e, "Could not load activity."));
    }
  }, [axiosInstance]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Card sx={CARD_SX}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <MDBox sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead sx={{ display: "table-header-group" }}>
            <TableRow>
              <TableCell>When</TableCell>
              <TableCell>What</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>From</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {events === null && !error && (
              <TableRow><TableCell colSpan={4} align="center"><CircularProgress size={24} /></TableCell></TableRow>
            )}
            {events?.length === 0 && (
              <TableRow><TableCell colSpan={4}>Nothing has happened yet.</TableCell></TableRow>
            )}
            {(events || []).map((e) => (
              <TableRow key={e.id}>
                <TableCell sx={{ whiteSpace: "nowrap" }}>{when(e.ts)}</TableCell>
                <TableCell>{EVENT_LABELS[e.event] || e.event}</TableCell>
                <TableCell sx={{ fontFamily: "monospace" }}>{e.client_id || "—"}</TableCell>
                <TableCell>{e.ip || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </MDBox>
      {next && (
        <MDBox mt={2} textAlign="center">
          <MDButton variant="outlined" color="info" size="small" onClick={() => load(next)}>Load more</MDButton>
        </MDBox>
      )}
    </Card>
  );
}

export default function DeveloperSettingsPage() {
  const navigate = useNavigate();
  const isV2 = useTheme()?.pearlUi === "v2";
  const { isAuthReady, isAuthenticated } = useAuth();
  const { isModuleEnabled, loading: entitlementsLoading } = useModuleEntitlements();
  const [tab, setTab] = useState(0);

  const licensed = !entitlementsLoading && isModuleEnabled("API_ACCESS");
  const ready = isAuthReady && isAuthenticated;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {isV2 ? (
          <PageHeader
            title="Developer & API"
            summary="OAuth clients for integrations that call the PearlCRM API."
            actions={licensed ? (
              <MDButton variant="outlined" color="dark" component="a" target="_blank" rel="noopener noreferrer"
                href={`${getApiBaseUrl().replace(/\/$/, "")}/api/v1/crm/docs`}>
                API reference
              </MDButton>
            ) : null}
          />
        ) : (
        <MDBox display="flex" alignItems="center" gap={1} mb={2}>
          <IconButton onClick={() => navigate("/settings")} aria-label="Back to settings">
            <ArrowBack />
          </IconButton>
          <Key sx={{ color: colors.brand.primary, fontSize: 32 }} />
          <MDBox flexGrow={1} ml={0.5}>
            <MDTypography variant="h5" fontWeight="medium">Developer &amp; API</MDTypography>
            <MDTypography variant="body2" color="text">
              OAuth clients for integrations that call the PearlCRM API.
            </MDTypography>
          </MDBox>
          {licensed && (
            <MDButton variant="text" color="info" component="a" target="_blank" rel="noopener noreferrer"
              href={`${getApiBaseUrl().replace(/\/$/, "")}/api/v1/crm/docs`}>
              API reference
            </MDButton>
          )}
        </MDBox>
        )}

        {entitlementsLoading && <MDBox py={6} textAlign="center"><CircularProgress /></MDBox>}

        {!entitlementsLoading && !licensed && (
          <Card sx={{ ...CARD_SX, textAlign: "center", py: 6 }}>
            <Lock sx={{ fontSize: 40, color: colors.status.neutral.dot, mx: "auto" }} />
            <MDTypography variant="h6" mt={1}>API access is an add-on</MDTypography>
            <MDTypography variant="body2" color="text">
              Ask your PearlCRM account manager to add API Access &amp; Integrations to your subscription.
            </MDTypography>
          </Card>
        )}

        {licensed && ready && (
          <>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, maxWidth: 360 }}>
              <Tab label="Clients" />
              <Tab label="Activity" />
            </Tabs>
            {tab === 0 && <ClientsTab />}
            {tab === 1 && <ActivityTab />}
          </>
        )}
      </MDBox>
    </DashboardLayout>
  );
}
